/**
 * Analytics Page
 * 
 * Phase 8.0: Real-time analytics dashboard with campaign performance metrics
 */

import { useState, useEffect } from "react";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Button,
  Spinner,
  DataTable,
  Badge,
} from "@shopify/polaris";
import { getAnalyticsOverview, type OverviewMetrics } from "../api/analytics";
import { isShopInstallRedirectError } from "../api/admin-api-client";

function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<OverviewMetrics | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getAnalyticsOverview();
      setOverview(data);
      setLastRefresh(new Date());
    } catch (err: any) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      console.error("[Analytics] Fetch error:", err);
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchAnalytics();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleManualRefresh = () => {
    fetchAnalytics();
  };

  const handleExportCSV = () => {
    if (!overview) return;

    // Prepare CSV data
    const csvRows = [
      "Metric,Value",
      `Total Plays,${overview.totalPlays}`,
      `Unique Players,${overview.uniquePlayers}`,
      `Rewards Granted,${overview.rewardsGranted}`,
      `Rewards Rejected,${overview.rewardsRejected}`,
      `Avg Session Duration (seconds),${overview.avgSessionDuration}`,
      `Conversion Rate (%),${overview.conversionRate}`,
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `analytics-${new Date().toISOString()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format duration in seconds to human-readable
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  return (
    <Page
      title="Analytics & Insights"
      subtitle="Campaign performance, conversion rates, and player engagement"
      primaryAction={{
        content: "Export CSV",
        onAction: handleExportCSV,
        disabled: !overview || loading,
      }}
      secondaryActions={[
        {
          content: autoRefresh ? "Auto-refresh: ON" : "Auto-refresh: OFF",
          onAction: () => setAutoRefresh(!autoRefresh),
        },
        {
          content: "Refresh Now",
          onAction: handleManualRefresh,
          loading,
        },
      ]}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical">
            <Text as="p">{error}</Text>
          </Banner>
        )}

        {loading && !overview && (
          <Card>
            <BlockStack gap="300" align="center">
              <Spinner size="large" />
              <Text as="p" tone="subdued">
                Loading analytics...
              </Text>
            </BlockStack>
          </Card>
        )}

        {overview && (
          <>
            <Banner tone="info">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="p" fontWeight="semibold">
                  📊 Live Analytics Dashboard
                </Text>
                <Text as="p" tone="subdued">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </Text>
              </InlineStack>
            </Banner>

            {/* Overview Cards */}
            <InlineStack gap="400" wrap>
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h3" variant="headingMd">
                      Total Plays
                    </Text>
                    {loading && <Spinner size="small" />}
                  </InlineStack>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {overview.totalPlays.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    Total number of game plays across all campaigns.
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h3" variant="headingMd">
                      Unique Players
                    </Text>
                    {loading && <Spinner size="small" />}
                  </InlineStack>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {overview.uniquePlayers.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    Distinct players (fingerprints).
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h3" variant="headingMd">
                      Rewards Granted
                    </Text>
                    {loading && <Spinner size="small" />}
                  </InlineStack>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {overview.rewardsGranted.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    Total rewards successfully issued.
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h3" variant="headingMd">
                      Conversion Rate
                    </Text>
                    {loading && <Spinner size="small" />}
                  </InlineStack>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {overview.conversionRate.toFixed(2)}%
                  </Text>
                  <Text as="p" tone="subdued">
                    Percentage of plays that resulted in a reward.
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h3" variant="headingMd">
                      Avg. Session Duration
                    </Text>
                    {loading && <Spinner size="small" />}
                  </InlineStack>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {formatDuration(overview.avgSessionDuration)}
                  </Text>
                  <Text as="p" tone="subdued">
                    Average time spent per session.
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h3" variant="headingMd">
                      Rewards Rejected
                    </Text>
                    {loading && <Spinner size="small" />}
                  </InlineStack>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {overview.rewardsRejected.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    Plays that hit rate limits or budget caps.
                  </Text>
                </BlockStack>
              </Card>
            </InlineStack>

            {/* Data Collection Info */}
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  📋 What We're Tracking
                </Text>
                <BlockStack gap="300">
                  <div>
                    <Text as="p" fontWeight="semibold">
                      ✅ PlaySession tracking
                    </Text>
                    <Text as="p" tone="subdued">
                      Campaign, variant, player, start/end timestamps, outcomes
                    </Text>
                  </div>
                  <div>
                    <Text as="p" fontWeight="semibold">
                      ✅ Event stream
                    </Text>
                    <Text as="p" tone="subdued">
                      play_start, play_end, reward_granted, idempotency_replay
                    </Text>
                  </div>
                  <div>
                    <Text as="p" fontWeight="semibold">
                      ✅ RewardGrant records
                    </Text>
                    <Text as="p" tone="subdued">
                      Full reward issuance history with Shopify discount codes
                    </Text>
                  </div>
                  <div>
                    <Text as="p" fontWeight="semibold">
                      ✅ Player fingerprinting
                    </Text>
                    <Text as="p" tone="subdued">
                      Device ID, fingerprint, customer ID linkage
                    </Text>
                  </div>
                </BlockStack>
              </BlockStack>
            </Card>

            {/* Coming Soon Banner */}
            <Banner tone="info">
              <BlockStack gap="200">
                <Text as="p" fontWeight="semibold">
                  📊 Coming in Phase 9 - Advanced Analytics
                </Text>
                <Text as="p">
                  • Top Campaigns leaderboard with detailed performance metrics
                  <br />
                  • Campaign-specific analytics pages
                  <br />
                  • Time-series charts (plays over time, conversion trends)
                  <br />
                  • Segment performance comparison
                </Text>
              </BlockStack>
            </Banner>
          </>
        )}
      </BlockStack>
    </Page>
  );
}

export default AnalyticsPage;
